import { useEffect, useState } from "react";
import { salesDocPath } from "../api/client";
import { notesPath, type NoteEntity } from "../api/notes";
import type { ProjectRow } from "../api/projects";
import type { CustomerList, ItemList, PurchaseMeta } from "../api/types";
import type { SalesDoc, SalesMeta } from "../api/sales-types";
import { t } from "../i18n";
import { fmtMoney } from "../ui/currency/money";
import { fmtDate, todayIst } from "../ui/format";
import { Drawer, Field, FormError } from "../ui/parts";
import { keptLookup } from "./offlineCache";
import { queueIssue, sendOrQueue, shrinkPhoto } from "./outbox";
import { shownTotal } from "./edit-core";
import type { OutboxKind, Receipt } from "./outbox-core";

/*
 * The offline screen's forms (after a reload with no connection, App.tsx → PwaUi.tsx OfflineApp), wave 6. Each one
 * writes an outbox item, exactly as the full screens do with no connection: same paths, same bodies, an Idempotency-Key
 * made now, the organization of the offline profile. Their lists (accounts, customers, items, projects, payment methods)
 * are the copies kept the last time those screens were opened online (offlineCache.ts "lookup"); without them a form says
 * what to open online first. Amounts include GST, as everywhere in the CRM; the server works out the rest when it records.
 */

export type OfflineForm =
  | { kind: "expense" }
  | { kind: "invoice" | "estimate"; issue?: boolean }
  | { kind: "time" }
  | { kind: "note"; entity: NoteEntity; id: string; name: string }
  | { kind: "payment"; doc: SalesDoc };

/** The first of these paths kept on the device; undefined while reading, null when none is. */
function useKept<T>(...paths: string[]) {
  const [v, setV] = useState<T | null | undefined>(undefined);
  const key = paths.join(" ");
  useEffect(() => {
    void (async () => {
      for (const p of key.split(" ")) {
        const got = await keptLookup<T>(p).catch(() => null);
        if (got) return setV(got);
      }
      setV(null);
    })();
  }, [key]);
  return v;
}

/** One drawer for every form: save queues the item and closes. Wave 8: `action` names the save button, `extra` adds one. */
export function Shell({ title, sub, busy, error, ready, onClose, onSave, children, action, extra }: { title: string; sub?: string; busy: boolean; error: string | null; ready: boolean; onClose: () => void; onSave: () => void; children: React.ReactNode; action?: string; extra?: React.ReactNode }) {
  return (
    <Drawer
      open
      title={title}
      sub={sub ?? t("Kept on this device and sent once when the server answers again.")}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onClose}>
            {t("Cancel")}
          </button>
          {extra}
          <button type="button" className="button button--primary" disabled={busy || !ready} onClick={onSave}>
            {busy ? t("Saving…") : (action ?? t("Keep for sending"))}
          </button>
        </>
      }
    >
      <div className="crm-form">{children}</div>
      <FormError message={error} />
    </Drawer>
  );
}

const Missing = ({ what }: { what: string }) => <p className="crm-note">{t("Nothing kept for this yet: open {what} once while online, then it works here too.", { what })}</p>;

export function OfflineFormDrawer({ form, me, onClose, onQueued }: { form: OfflineForm; me: string; onClose: () => void; onQueued: (label: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Wave 8: a new invoice issued at once with the next number reserved to this device (outbox.ts queueIssue).
  const runIssue = async (label: string, run: () => Promise<string>) => {
    setBusy(true);
    setError(null);
    try {
      onQueued(t("{label}, issued offline as {number}", { label, number: await run() }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const queue = async (kind: OutboxKind, label: string, path: string, body: unknown, receipt: Receipt | null = null) => {
    setBusy(true);
    setError(null);
    try {
      await sendOrQueue({ kind, label, path, body, receipt });
      onQueued(label);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  if (form.kind === "expense") return <ExpenseForm busy={busy} error={error} onClose={onClose} queue={queue} />;
  if (form.kind === "invoice" || form.kind === "estimate") return <DocForm kind={form.kind} busy={busy} error={error} onClose={onClose} queue={queue} issue={form.kind === "invoice" && form.issue ? (label, run) => void runIssue(label, run) : undefined} />;
  if (form.kind === "time") return <TimeForm me={me} busy={busy} error={error} onClose={onClose} queue={queue} />;
  if (form.kind === "note") return <NoteForm entity={form.entity} id={form.id} name={form.name} busy={busy} error={error} onClose={onClose} queue={queue} />;
  if (form.kind === "payment") return <PaymentForm doc={form.doc} busy={busy} error={error} onClose={onClose} queue={queue} />;
  return null;
}

type FormProps = { busy: boolean; error: string | null; onClose: () => void; queue: (kind: OutboxKind, label: string, path: string, body: unknown, receipt?: Receipt | null) => Promise<void> };
const amountOk = (s: string) => /^\d{1,10}(\.\d{1,2})?$/.test(s.replace(/[,\s₹]/g, "")) && Number(s.replace(/[,\s₹]/g, "")) > 0;
const clean = (s: string) => s.replace(/[,\s₹]/g, "");

function ExpenseForm({ busy, error, onClose, queue }: FormProps) {
  const meta = useKept<PurchaseMeta>("/admin/purchases/meta");
  const [date, setDate] = useState(todayIst());
  const [amount, setAmount] = useState("");
  const [account, setAccount] = useState("exp_other");
  const [paid, setPaid] = useState("pt_bank");
  const [reference, setReference] = useState("");
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const accounts = meta?.accounts ?? [];
  function save() {
    if (!amountOk(amount)) return setErr(t("Enter the amount."));
    setErr(null);
    const category = accounts.find((a) => a.id === account)?.name ?? t("Expense");
    void queue("expense", t("Expense {amount} · {category} · {date}", { amount: fmtMoney(clean(amount)), category, date: fmtDate(date) }), "/admin/purchases/expenses", { date, account_id: account, paid_through_id: paid, amount: clean(amount), gst_rate: 0, itc_eligible: false, reference: reference.trim() || null }, receipt);
  }
  return (
    <Shell title={t("New expense")} busy={busy} error={err ?? error} ready={Boolean(meta)} onClose={onClose} onSave={save}>
      {meta === null && <Missing what={t("Expenses")} />}
      <Field label={t("Date")}>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
      <Field label={t("Amount (₹, including GST)")}>
        <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </Field>
      <Field label={t("Expense account")}>
        <select value={account} onChange={(e) => setAccount(e.target.value)}>
          {accounts.filter((a) => a.group === "expense").map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t("Paid through")}>
        <select value={paid} onChange={(e) => setPaid(e.target.value)}>
          {accounts.filter((a) => a.group === "paid_through").map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t("Reference")}>
        <input value={reference} maxLength={120} onChange={(e) => setReference(e.target.value)} />
      </Field>
      <Field label={t("Receipt photo")} hint={receipt ? receipt.name : undefined}>
        <input type="file" accept="image/*" capture="environment" onChange={(e) => e.target.files?.[0] && void shrinkPhoto(e.target.files[0]).then(setReceipt)} />
      </Field>
    </Shell>
  );
}

type Line = { item_id: string; name: string; quantity: string; rate: string };

function DocForm({ kind, busy, error, onClose, queue, issue }: FormProps & { kind: "invoice" | "estimate"; issue?: (label: string, run: () => Promise<string>) => void }) {
  const customers = useKept<CustomerList>("/admin/customers?status=active");
  const items = useKept<ItemList>("/admin/items?status=active");
  const [customer, setCustomer] = useState("");
  const [date, setDate] = useState(todayIst());
  const [lines, setLines] = useState<Line[]>([{ item_id: "", name: "", quantity: "1", rate: "" }]);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const set = (i: number, patch: Partial<Line>) => setLines((ls) => ls.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  function save(issueNow = false) {
    if (!customer) return setErr(t("Choose the customer."));
    const ok = lines.filter((l) => (l.item_id || l.name.trim()) && Number(l.quantity) > 0);
    if (!ok.length) return setErr(t("Add at least one line with an item or a name and a quantity."));
    if (ok.some((l) => !l.item_id && !amountOk(l.rate))) return setErr(t("Enter the rate of each line that has no item."));
    setErr(null);
    const name = customers?.items.find((c) => c.id === customer)?.display_name ?? "";
    const body = {
      customer_id: customer,
      issue_date: date,
      notes: notes.trim() || null,
      lines: ok.map((l) => ({ item_id: l.item_id || null, name: l.item_id ? null : l.name.trim(), quantity: l.quantity, rate: l.rate.trim() ? clean(l.rate) : null })),
    };
    const label = kind === "invoice" ? t("Draft invoice · {customer}", { customer: name }) : t("Draft estimate · {customer}", { customer: name });
    if (issueNow && issue) {
      // What this device shows: each line at its typed rate, or its item's kept rate (GST included, as on the form).
      const rated = body.lines.map((l) => ({ ...l, rate: l.rate ?? String((items?.items ?? []).find((it) => it.id === l.item_id)?.rate ?? "0") }));
      return issue(name, () => queueIssue({ label: name, day: date, draft: body, shown: { total: shownTotal({ lines: rated }) } }));
    }
    void queue(kind, label, salesDocPath(kind, "").replace(/\/$/, ""), body);
  }
  return (
    <Shell
      title={kind === "invoice" ? t("New draft invoice") : t("New draft estimate")}
      sub={issue ? t("Saved as a draft when the server answers, or issued now with the next invoice number reserved to this device (not emailed).") : t("Saved as a draft when the server answers; issue it from Invoices or Estimates then.")}
      busy={busy}
      error={err ?? error}
      ready={Boolean(customers)}
      onClose={onClose}
      onSave={() => save()}
      extra={
        issue && (
          <button type="button" className="button button--secondary" disabled={busy || !customers} onClick={() => save(true)}>
            {t("Issue offline")}
          </button>
        )
      }
    >
      {customers === null && <Missing what={t("an invoice or estimate form")} />}
      <Field label={t("Customer")}>
        <select value={customer} onChange={(e) => setCustomer(e.target.value)}>
          <option value="">{t("Choose…")}</option>
          {(customers?.items ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.display_name}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t("Date")}>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
      {lines.map((l, i) => (
        <fieldset key={i} className="pwa-line">
          <legend>{t("Line {n}", { n: i + 1 })}</legend>
          <Field label={t("Item")}>
            <select value={l.item_id} onChange={(e) => set(i, { item_id: e.target.value })}>
              <option value="">{t("Not in the catalog (type a name)")}</option>
              {(items?.items ?? []).map((it) => (
                <option key={it.id} value={it.id}>
                  {it.name}
                </option>
              ))}
            </select>
          </Field>
          {!l.item_id && (
            <Field label={t("Name")}>
              <input value={l.name} maxLength={200} onChange={(e) => set(i, { name: e.target.value })} />
            </Field>
          )}
          <Field label={t("Quantity")}>
            <input inputMode="decimal" value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
          </Field>
          <Field label={t("Rate (₹, including GST)")} hint={l.item_id ? t("Blank: the item's rate.") : undefined}>
            <input inputMode="decimal" value={l.rate} onChange={(e) => set(i, { rate: e.target.value })} />
          </Field>
        </fieldset>
      ))}
      <p>
        <button type="button" className="button button--secondary button--sm" onClick={() => setLines((ls) => [...ls, { item_id: "", name: "", quantity: "1", rate: "" }])}>
          {t("Add a line")}
        </button>
      </p>
      <Field label={t("Customer notes")}>
        <textarea value={notes} rows={2} maxLength={1000} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Shell>
  );
}

function TimeForm({ me, busy, error, onClose, queue }: FormProps & { me: string }) {
  const projects = useKept<ProjectRow[]>("/admin/projects", "/admin/projects?status=active");
  const [project, setProject] = useState("");
  const [date, setDate] = useState(todayIst());
  const [time, setTime] = useState("");
  const [billable, setBillable] = useState(true);
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState<string | null>(null);
  function save() {
    if (!project) return setErr(t("Choose the project."));
    if (!/^\d{1,2}(:\d{2}|\.\d{1,2})?$/.test(time.trim())) return setErr(t("Enter the time as hours (1.5) or hours:minutes (1:30)."));
    setErr(null);
    const name = projects?.find((p) => p.id === project)?.name ?? "";
    void queue("time", t("Time {time} h · {project} · {date}", { time: time.trim(), project: name, date: fmtDate(date) }), "/admin/projects/time-entries", { project_id: project, task_id: null, staff_id: me, date, hours: time.trim(), billable, notes: notes.trim() || null });
  }
  return (
    <Shell title={t("Log time")} busy={busy} error={err ?? error} ready={Boolean(projects)} onClose={onClose} onSave={save}>
      {projects === null && <Missing what={t("Projects")} />}
      <Field label={t("Project")}>
        <select value={project} onChange={(e) => setProject(e.target.value)}>
          <option value="">{t("Choose…")}</option>
          {(projects ?? []).filter((p) => p.status === "active").map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} · {p.customer_name}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t("Date")}>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>
      <Field label={t("Time")} hint={t("Hours (1.5) or hours:minutes (1:30).")}>
        <input inputMode="decimal" value={time} onChange={(e) => setTime(e.target.value)} />
      </Field>
      <label className="crm-check">
        <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} /> {t("Billable")}
      </label>
      <Field label={t("Notes")}>
        <textarea value={notes} rows={2} maxLength={1000} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </Shell>
  );
}

function NoteForm({ entity, id, name, busy, error, onClose, queue }: FormProps & { entity: NoteEntity; id: string; name: string }) {
  const [text, setText] = useState("");
  return (
    <Shell title={t("Note on {name}", { name })} busy={busy} error={error} ready={Boolean(text.trim())} onClose={onClose} onSave={() => void queue("note", t("Note on {name}", { name }), notesPath(entity, id), { body: text.trim() })}>
      <Field label={t("Note")}>
        <textarea value={text} rows={4} maxLength={2000} onChange={(e) => setText(e.target.value)} placeholder={t("A call, a promise, what was agreed…")} />
      </Field>
    </Shell>
  );
}

function PaymentForm({ doc, busy, error, onClose, queue }: FormProps & { doc: SalesDoc }) {
  const meta = useKept<SalesMeta>("/admin/sales/meta");
  const [amount, setAmount] = useState(String(Number(doc.balance)));
  const [method, setMethod] = useState("upi");
  const [reference, setReference] = useState("");
  const [on, setOn] = useState(todayIst());
  const [err, setErr] = useState<string | null>(null);
  const methods = meta?.methods?.length ? meta.methods : [{ value: "upi", label: "UPI" }];
  function save() {
    if (!amountOk(amount)) return setErr(t("Enter the amount."));
    setErr(null);
    void queue("payment", t("Payment {amount} on {label} · {customer_name}", { amount: fmtMoney(clean(amount), doc), label: doc.number ?? doc.label, customer_name: doc.customer_name }), `${salesDocPath("invoice", doc.id)}/payments`, { amount: clean(amount), method, reference: reference.trim() || null, received_on: on, note: null });
  }
  return (
    <Shell title={t("Record payment")} sub={t("{balance} was due when this copy was saved. The server checks it again when the payment is sent.", { balance: fmtMoney(doc.balance, doc) })} busy={busy} error={err ?? error} ready onClose={onClose} onSave={save}>
      <Field label={t("Amount received")}>
        <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </Field>
      <Field label={t("Method")}>
        <select value={method} onChange={(e) => setMethod(e.target.value)}>
          {methods.map((m) => (
            <option key={m.value} value={m.value}>
              {t(m.label)}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t("Reference")}>
        <input value={reference} maxLength={120} onChange={(e) => setReference(e.target.value)} />
      </Field>
      <Field label={t("Received on")}>
        <input type="date" value={on} onChange={(e) => setOn(e.target.value)} />
      </Field>
    </Shell>
  );
}
